import * as fs from 'node:fs';
import * as path from 'node:path';
import type { WorktreeBaseBranches } from '@buildautomaton/types';
import { normalizeRepoPathRelativeToWorkspaceRoot } from '../../git/changes/repo/normalize-repo-path-relative-to-workspace-root.js';
import { gitWorktreeAddBranch } from '../../git/worktrees/worktree-add.js';
import { gitWorktreeAddExistingBranch } from '../../git/worktrees/worktree-add-existing-branch.js';
import type { LogFn } from '../../log.js';
import type { WorktreeLayoutKind } from '../worktree-layout-kind.js';
import { copyBridgeFilesAlongWorktreePath } from './copy-bridge-files-along-worktree-path.js';
import { resolveBaseRefForRepo } from './resolve-base-ref-for-repo.js';

export async function addIsolatedRepoWorktree(options: {
  kind: WorktreeLayoutKind;
  bridgeResolved: string;
  scopeDir: string;
  repoAbsolutePath: string;
  branch: string;
  existingBranch?: string;
  worktreeBaseBranches?: WorktreeBaseBranches;
  log: LogFn;
}): Promise<string | null> {
  const {
    kind,
    bridgeResolved,
    scopeDir,
    repoAbsolutePath,
    branch,
    existingBranch,
    worktreeBaseBranches,
    log,
  } = options;

  const repoPathRelativeToBridgeRootRaw = path.relative(bridgeResolved, repoAbsolutePath);
  if (repoPathRelativeToBridgeRootRaw.startsWith('..') || path.isAbsolute(repoPathRelativeToBridgeRootRaw)) {
    return null;
  }

  const repoPathRelativeToBridgeRoot = normalizeRepoPathRelativeToWorkspaceRoot(
    repoPathRelativeToBridgeRootRaw === '' ? '.' : repoPathRelativeToBridgeRootRaw,
  );
  const worktreePath =
    repoPathRelativeToBridgeRoot === '.'
      ? scopeDir
      : path.join(scopeDir, repoPathRelativeToBridgeRoot);

  if (repoPathRelativeToBridgeRoot !== '.') {
    fs.mkdirSync(path.dirname(worktreePath), { recursive: true });
    copyBridgeFilesAlongWorktreePath({
      bridgeRoot: bridgeResolved,
      scopeDir,
      repoPathRelativeToBridgeRoot,
      log,
    });
  }

  const checkoutBranch = existingBranch?.trim() || branch;
  try {
    if (existingBranch?.trim()) {
      await gitWorktreeAddExistingBranch(repoAbsolutePath, worktreePath, checkoutBranch);
      log(`[worktrees] Added ${kind} worktree ${worktreePath} (existing branch ${checkoutBranch}).`);
    } else {
      const baseRef = resolveBaseRefForRepo(repoPathRelativeToBridgeRoot, worktreeBaseBranches);
      await gitWorktreeAddBranch(repoAbsolutePath, worktreePath, branch, baseRef);
      log(`[worktrees] Added ${kind} worktree ${worktreePath} (branch ${branch}, base ${baseRef}).`);
    }
    return worktreePath;
  } catch (e) {
    log(
      `[worktrees] Worktree add failed for ${repoAbsolutePath}: ${e instanceof Error ? e.message : String(e)}`,
    );
    return null;
  }
}
